import { useTranslation } from 'react-i18next';

import { Icon, Tag } from '~/components';
import { cn } from '~/lib/cn';

import type { CampaignStatus } from '../../types';

type StatusStyle = 'info' | 'success' | 'warning' | 'error' | 'neutral';

const STATUS_STYLE: Record<CampaignStatus, StatusStyle> = {
  NEW: 'info',
  SUBMITTED: 'info',
  ACCEPTED: 'info',
  LIVE: 'success',
  COMPLETED: 'success',
  FINALIZED: 'success',
  PARTIALLY_REFUNDED: 'warning',
  REQUEST_CANCELLATION: 'warning',
  CANCELLATION_REJECTED: 'warning',
  REJECTED: 'error',
  CANCELLED: 'error',
  CREATION_FAILED: 'error',
};

const STYLE_DOT_CLASS: Record<StatusStyle, string> = {
  info: 'text-info-default',
  success: 'text-success-default',
  warning: 'text-warning-default',
  error: 'text-error-default',
  neutral: 'text-subtle',
};

const STATUS_TO_I18N: Record<CampaignStatus, string> = {
  NEW: 'new',
  SUBMITTED: 'submitted',
  ACCEPTED: 'accepted',
  LIVE: 'live',
  REJECTED: 'rejected',
  COMPLETED: 'completed',
  REQUEST_CANCELLATION: 'requestCancellation',
  CANCELLATION_REJECTED: 'cancellationRejected',
  CANCELLED: 'cancelled',
  PARTIALLY_REFUNDED: 'partiallyRefunded',
  CREATION_FAILED: 'creationFailed',
  FINALIZED: 'finalized',
};

interface CampaignStatusTagProps {
  status: CampaignStatus;
}

export function CampaignStatusTag({ status }: CampaignStatusTagProps) {
  const { t } = useTranslation('automated-creator-campaign', {
    keyPrefix: 'status',
  });
  const style = STATUS_STYLE[status] ?? 'neutral';

  return (
    <Tag size="sm" variant="static">
      <Icon
        type="circle"
        size="md"
        className={cn('rounded-full', STYLE_DOT_CLASS[style])}
      />
      <span>{t(STATUS_TO_I18N[status] ?? status)}</span>
    </Tag>
  );
}

CampaignStatusTag.displayName = 'CampaignStatusTag';
